/**
 * Reverie — every fixed value the scroll choreography is built on.
 * Geometry, timing and easing live here so the render loop stays readable.
 */

/* ── collection ─────────────────────────────────────────────── */

export const IMG_COUNT = 11

/** 0-based index of the pinned opener frame (rev-11). Never appears on the wall. */
export const HERO_IMG = 10

/**
 * The frames the mosaic wall draws from, in order. The first `CHOREO - 1` of
 * these are what the fan riffles through, so the strongest frames lead.
 *
 * The length matters: the wall keys tiles off `(col + row * 4) % length`, and
 * with 9 the offsets between touching cells (1, 3, 4 and 5) never land back on
 * the same frame, so no two adjacent tiles repeat.
 */
export const GRID_POOL = [2, 5, 8, 0, 6, 4, 3, 1, 9]

/**
 * Sources by collection index, matching the printed numbering (index 0 is № 01).
 * Index 7 is deliberately absent, a frame held back from the series, so the
 * indices below still line up with the captions.
 *
 * Each file is named for its own frame. Never re-use one of these names for
 * different bytes: the browser, the image optimiser and the CDN all key on the
 * URL, so a swapped frame at an old name can keep serving the old picture.
 */
export const IMAGE_SOURCES: (string | null)[] = [
    '/assets/reverie/rev-01-ochre-hour.png',
    '/assets/reverie/rev-02-warm-static.jpg',
    '/assets/reverie/rev-03-the-unnamed.jpg',
    '/assets/reverie/rev-04-dreamt-gold.jpg',
    '/assets/reverie/rev-05-recalled-dusk.png',
    '/assets/reverie/rev-06-slow-ember.jpg',
    '/assets/reverie/rev-07-sunless-noon.jpg',
    null,
    '/assets/reverie/rev-09-the-threshold.png',
    '/assets/reverie/rev-10-the-witness.png',
    '/assets/reverie/rev-11-neon-silence.jpg'
]

export const CAPTIONS = [
    'Ochre Hour',
    'Warm Static',
    'The Unnamed',
    'Dreamt Gold',
    'Recalled Dusk',
    'Slow Ember',
    'Sunless Noon',
    'Never Rose',
    'The Threshold',
    'The Witness',
    'Neon Silence'
]

export const DETAILS = [
    'a morning that never happened',
    'remembered in warm colour',
    'the light before it had a name',
    'a face the machine dreamed',
    'dusk, recalled from nothing',
    'the hour that never rose',
    'warmth without a sun',
    'a sunrise that never was',
    'a door left open in the dark',
    'everything it saw, it kept',
    'lit red on one side, blue on the other'
]

export const PLACARD_LINE = 'Imagined Light · Generative Series · MMXXVI'

/* ── the manifesto ──────────────────────────────────────────── */

export const POEM: [string, string][] = [
    ['I STOPPED CHASING', 'THE PERFECT LIGHT'],
    ['IT ARRIVES', 'WHEN IT WANTS'],
    ['EVERY MORNING', 'RENDERS ITSELF'],
    ['NOTHING HERE', 'WAS PHOTOGRAPHED'],
    ['ONLY', 'REMEMBERED'],
    ['THE MACHINE DREAMS', 'IN WARM COLOUR'],
    ['A THOUSAND SUNRISES', 'THAT NEVER ROSE'],
    ['STAY UNTIL', 'THE GRAIN SETTLES'],
    ['THEN LET IT', 'SCATTER']
]

/* ── the title ──────────────────────────────────────────────── */

export const TITLE_EYEBROW = 'Generative Series — MMXXVI'
export const TITLE_DISPLAY = 'Imagined Light'
export const TITLE_SUBLINE = 'a collection of mornings that never happened'

/* ── cards ──────────────────────────────────────────────────── */

/** Total cards in the plane. */
export const N = 48

/** Story cards: the hero plus one distinct pool frame each, so the fan never repeats. */
export const CHOREO = 8
export const CHALF = CHOREO / 2
export const CR = CHALF / 2

/* ── timeline (all in prog) ─────────────────────────────────── */

export const STOP_DECK = 0.28
export const STOP_FAN = 0.44
export const STOP_SPLIT = 0.58
export const STOP_COLS = 0.7
export const STOP_GRID = 0.82

export const P_POEM_FADE: [number, number] = [0.05, 0.18]
export const P_TITLE_IN: [number, number] = [0.46, 0.6]
export const P_TITLE_OUT: [number, number] = [0.72, 0.8]

/* ── input & easing ────────────────────────────────────────── */

export const WHEEL_K = 0.0011
export const WHEEL_CLAMP = 140
export const LERP_BASE = 0.09
export const PAN_BASE = 0.06
export const HOVER_BASE = 0.004
export const HOVER_GLOBAL_BASE = 0.005
export const OPEN_IN_BASE = 0.055
export const OPEN_OUT_BASE = 0.006
export const FLICK_DECAY = 0.02
export const DRAG_GAIN = 1.3
export const SCRUB_PX = 6
export const DT_CAP = 0.05

/* ── layout ─────────────────────────────────────────────────── */

export const MOBILE_W = 760
export const GRID_FILL = 0.84
export const FAN_DEPTH = 0.46
export const PAN_INSET = 0.82
export const DPR_CAP = 2

/**
 * Ceiling on the hero canvas backing store. The hero element is deliberately
 * larger than the viewport, so at full device pixel ratio its canvas would run
 * into browser surface limits. The source frames top out below this anyway.
 */
export const HERO_MAX_PX = 9_000_000

/* ── intro envelope ────────────────────────────────────────── */

export const INTRO_MS = 1400
export const INTRO_DELAY_MS = 180
export const INTRO_AMP = 0.035
export const COVER_FADE_S = 0.4
export const KEN_BURNS_MS = 8000

/* ── open print ────────────────────────────────────────────── */

export const CAP_H = 74
